import { useState, useEffect, useRef, useCallback } from 'react';
import keycloak from '../services/keycloak';
import { authService } from '../services';

/**
 * Hook to handle session timeout logic with idle detection.
 * Set to 2 hours of inactivity.
 * 
 * @param {Object} options
 * @param {number} options.warningThreshold - Seconds before inactivity expiration to show warning (default: 60)
 * @param {Function} options.onLogout - Callback function to execute on logout
 */
const useSessionTimeout = ({ warningThreshold = 60, onLogout } = {}) => {
    // Configuration: 2 hours of total inactivity allowed
    const MAX_IDLE_SECONDS = 7200; // 2 hours

    const [timeLeft, setTimeLeft] = useState(null);
    const [showWarning, setShowWarning] = useState(false);
    const [isExpired, setIsExpired] = useState(false);
    const timerRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const showWarningRef = useRef(false);
    const isRefreshingRef = useRef(false);

    // Keep ref in sync with state for use inside non-dependency-tracked logic
    useEffect(() => {
        showWarningRef.current = showWarning;
    }, [showWarning]);

    /**
     * Main monitor logic:
     * 1. Checks idle time (last activity vs now)
     * 2. Checks token expiration (to handle server-side expiry or network loss)
     * 3. Refreshes token silently if user is still active or in safe idle zone
     */
    const monitorSession = useCallback(async () => {
        if (!keycloak.tokenParsed) return;

        const now = Date.now();
        const idleSeconds = Math.floor((now - lastActivityRef.current) / 1000);

        // 1. HARD INACTIVITY LOGOUT (2 hours reached)
        if (idleSeconds >= MAX_IDLE_SECONDS) {
            console.log('[SessionTimeout] Inactivity limit reached (2 hours). Logging out.');
            setIsExpired(true);
            setShowWarning(false);
            setTimeLeft(0);
            if (onLogout) onLogout();
            return;
        }

        // 2. SHOW WARNING MODAL (Within the threshold of the 2 hour limit)
        if (idleSeconds >= (MAX_IDLE_SECONDS - warningThreshold)) {
            setShowWarning(true);
            setTimeLeft(MAX_IDLE_SECONDS - idleSeconds);
            setIsExpired(false);
        } else {
            // User is still active or in the "safe" zone
            if (showWarningRef.current) setShowWarning(false);
            setIsExpired(false);
        }

        // 3. TOKEN VALIDITY & SILENT REFRESH
        // Get expiration time from token (exp is in seconds)
        const exp = keycloak.tokenParsed.exp;
        const timeSkew = keycloak.timeSkew || 0;
        const currentTimeInSecs = Math.floor(now / 1000) + timeSkew;
        const tokenRemaining = exp - currentTimeInSecs;

        // If the token is about to expire (e.g. < 60s left), but the user
        // hasn't reached the INACTIVITY warning zone yet, we refresh it silently.
        // This allows the user to stay idle for the full 2 hours even if 
        // individual tokens only last 5 minutes.
        if (tokenRemaining < 60 && tokenRemaining > 0 && !showWarningRef.current) {
            if (!isRefreshingRef.current) {
                isRefreshingRef.current = true;
                try {
                    await authService.updateToken(30);
                } catch (error) {
                    console.error('[SessionTimeout] Silent refresh failed:', error);
                } finally {
                    isRefreshingRef.current = false;
                }
            }
        }

        // 4. SERVER EXPIRY / REFRESH FAILURE
        // If the token is actually expired and couldn't be refreshed, logout immediately to prevent working on dead sessions.
        if (tokenRemaining <= 0) {
            if (!isRefreshingRef.current) {
                isRefreshingRef.current = true;
                try {
                    console.log('[SessionTimeout] Token expired (remaining <= 0), attempting recovery refresh on wake/resume...');
                    const refreshed = await authService.updateToken(30);
                    isRefreshingRef.current = false;
                    if (refreshed && keycloak.tokenParsed && (keycloak.tokenParsed.exp - Math.floor(Date.now() / 1000) > 0)) {
                        return; // Token restored successfully!
                    }
                } catch (error) {
                    console.error('[SessionTimeout] Recovery refresh failed:', error);
                    isRefreshingRef.current = false;
                }
            } else {
                return;
            }

            console.warn('[SessionTimeout] Token expired and cannot be renewed. Redirecting to login page.');
            setIsExpired(true);
            setShowWarning(false);
            setTimeLeft(0);
            if (onLogout) onLogout();
        }

    }, [onLogout, warningThreshold]);

    // Set up the heartbeat check every 5 seconds and focus/visibility changes
    useEffect(() => {
        // Run check immediately
        monitorSession();

        // Check every 5 seconds
        timerRef.current = setInterval(monitorSession, 5000);

        const handleFocusOrVisibility = () => {
            console.log('[SessionTimeout] Window visible or focused, checking session immediately...');
            monitorSession();
        };

        window.addEventListener('focus', handleFocusOrVisibility);
        document.addEventListener('visibilitychange', handleFocusOrVisibility);

        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
            window.removeEventListener('focus', handleFocusOrVisibility);
            document.removeEventListener('visibilitychange', handleFocusOrVisibility);
        };
    }, [monitorSession]);

    /**
     * Resets the inactivity timer (called by user via Modal button)
     */
    const extendSession = useCallback(async () => {
        try {
            console.log('[SessionTimeout] Resetting inactivity timer (User Action)');
            lastActivityRef.current = Date.now();
            setShowWarning(false);
            setIsExpired(false);

            if (!isRefreshingRef.current) {
                isRefreshingRef.current = true;
                try {
                    // Ensure token is topped up
                    await authService.updateToken(300);
                } finally {
                    isRefreshingRef.current = false;
                }
            }

            return true;
        } catch (error) {
            console.error("[SessionTimeout] Failed to extend session:", error);
            return false;
        }
    }, []);

    /**
     * Activity Detection: Listens for user events to reset the idle timer.
     * Throttled to every 2 seconds to avoid unnecessary state/ref updates.
     */
    useEffect(() => {
        const handleActivity = () => {
            const now = Date.now();
            if (now - lastActivityRef.current < 2000) return;

            // If the warning modal is NOT showing, we update the timestamp.
            // If it IS showing, we stop updating so the user must interact with the modal.
            if (!showWarningRef.current) {
                lastActivityRef.current = now;
            }
        };

        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'];
        events.forEach(event => window.addEventListener(event, handleActivity));

        return () => {
            events.forEach(event => window.removeEventListener(event, handleActivity));
        };
    }, []);

    return {
        timeLeft,
        showWarning,
        isExpired,
        extendSession
    };
};

export default useSessionTimeout;
